import React, { useEffect, useState, useRef, useCallback } from 'react';
import { adminProductApi } from '../../../../api/adminProductApi';
import { parseApiError, normalizeBarcode as normalizeCode } from './BarcodeScanField';

/**
 * Редагування артикула (код товару). Аналогічно штрихкоду: blur / Enter — зберегти.
 * Порожнє значення не допускається (бекенд поверне помилку).
 */
const ProductCodeScanField = ({
  product,
  onCodeSaved,
  onCodeError,
  wrapper = 'td',
  wrapperClassName = '',
  inputClassName = 'form-control form-control-sm barcode-scan-input'
}) => {
  const inputRef = useRef(null);
  const [focused, setFocused] = useState(false);
  const [val, setVal] = useState(() => (product.code != null ? String(product.code) : ''));

  useEffect(() => {
    if (!focused) {
      setVal(product.code != null ? String(product.code) : '');
    }
  }, [product.code, product.id, focused]);

  const commit = useCallback(
    async (raw) => {
      const next = normalizeCode(raw);
      const prev = normalizeCode(product.code);
      if (next === prev) return;
      try {
        await adminProductApi.patchProductCode(product.id, next);
        onCodeSaved(product.id, next);
      } catch (err) {
        onCodeError(parseApiError(err));
        setVal(product.code != null ? String(product.code) : '');
      }
    },
    [product.code, product.id, onCodeSaved, onCodeError]
  );

  const handleFocus = () => {
    setFocused(true);
    const had = normalizeCode(product.code).length > 0;
    if (had) {
      requestAnimationFrame(() => {
        try {
          inputRef.current?.select();
        } catch (e) {
          /* ignore */
        }
      });
    }
  };

  const handleBlur = () => {
    setFocused(false);
    const fromInput = inputRef.current != null ? inputRef.current.value : val;
    commit(fromInput);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      inputRef.current?.blur();
    }
  };

  const input = (
    <input
      ref={inputRef}
      type='text'
      autoComplete='off'
      spellCheck={false}
      className={inputClassName}
      style={{ fontSize: '0.875rem' }}
      value={val}
      onChange={(e) => setVal(e.target.value)}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      onClick={(e) => e.stopPropagation()}
      title='Клік — редагувати. Enter або вихід з поля — зберегти.'
    />
  );

  if (wrapper === 'div') {
    return (
      <div
        className={wrapperClassName}
        onClick={() => inputRef.current?.focus()}
        role='presentation'
      >
        {input}
      </div>
    );
  }

  return (
    <td
      className={wrapperClassName || 'text-monospace small p-1 align-middle'}
      style={{ minWidth: '8rem', maxWidth: '14rem' }}
      onClick={() => inputRef.current?.focus()}
    >
      {input}
    </td>
  );
};

export default ProductCodeScanField;
